import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, makeStyles, createMuiTheme } from '@material-ui/core';
import _ from 'lodash';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ACTIONABLE_TICKET_MAPPING } from '../../../comm/constant';
import { useActionableSettings, useActionableTicketReminder } from '../../../utils/hooks';
import { useQuery } from '../../../utils/useQuery';
import { userRoleFunc } from '../../../utils/userRole';
import rou from '../../../router';
import ActionableCard from './ActionableCard';
import ActionableHeader from './ActionableHeader';

const theme = createMuiTheme({
  breakpoints: {
    values: {
      md: 1024,
      lg: 1680,
    },
  },
});

const useStyles = makeStyles(() => ({
  root: {
    height: '100vh',
    minHeight: '100vh',
    backgroundColor: '#FFFFFF',
    display: 'flex',
    flexDirection: 'column',
  },
  '@keyframes backgroundAnimation': {
    '0%': { backgroundColor: '#FFFFFF' },
    '50%': { backgroundColor: 'black' },
    '100%': { backgroundColor: '#FFFFFF' },
  },
  flash: {
    animation: '$backgroundAnimation 2s infinite',
    [theme.breakpoints.down('md')]: {
      paddingTop: '20rem',
    },
    [theme.breakpoints.only('md')]: {
      paddingTop: '8rem',
    },
    [theme.breakpoints.up('lg')]: {
      paddingTop: '8rem',
    },
  },
  noflash: {
    [theme.breakpoints.down('md')]: {
      paddingTop: '20rem',
    },
    [theme.breakpoints.only('md')]: {
      paddingTop: '8rem',
    },
    [theme.breakpoints.up('lg')]: {
      paddingTop: '8rem',
    },
  },
}));

// 读取当前页面的rem大小，用来对齐原来的card尺寸。
const getRemSize = () => {
  return parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 14;
};

// CSS没算出gap时，用页面原本的gap做兜底。
const parseCssPixel = (value, fallback = 0) => {
  const result = parseFloat(value);
  return Number.isNaN(result) ? fallback : result;
};

// 取得当前断点下原本的card尺寸，默认能放下时不改变UI。
const getDefaultCardSize = () => {
  const remSize = getRemSize();
  const width = window.innerWidth;
  let cardSize = 13.75 * remSize;

  if (width >= 1680) {
    cardSize = 23 * remSize;
  } else if (width >= 1540) {
    cardSize = 11.75 * remSize;
  }

  return cardSize + 4 * remSize;
};

// 根据容器大小和card数量，计算一屏内可容纳全部card的最大尺寸。
const getFittedCardSize = (container, count) => {
  if (!container || count <= 0) {
    return null;
  }

  const style = window.getComputedStyle(container);
  const fallbackGap = 3.75 * getRemSize();
  const columnGap = parseCssPixel(style.columnGap || style.gap, fallbackGap);
  const rowGap = parseCssPixel(style.rowGap || style.gap, fallbackGap);
  const horizontalPadding = parseCssPixel(style.paddingLeft) + parseCssPixel(style.paddingRight);
  const verticalPadding = parseCssPixel(style.paddingTop) + parseCssPixel(style.paddingBottom);
  const containerRect = container.getBoundingClientRect();
  const contentWidth = containerRect.width - horizontalPadding;
  const contentHeight = containerRect.height - verticalPadding;
  let fittedSize = 0;

  // 尝试不同列数，取宽高都能放下的最大正方形尺寸。
  for (let columns = 1; columns <= count; columns += 1) {
    const rows = Math.ceil(count / columns);
    const maxWidth = (contentWidth - columnGap * (columns - 1)) / columns;
    const maxHeight = (contentHeight - rowGap * (rows - 1)) / rows;
    fittedSize = Math.max(fittedSize, Math.min(maxWidth, maxHeight));
  }

  const defaultSize = getDefaultCardSize();
  const safeSize = Math.floor(fittedSize - 1);
  if (safeSize >= defaultSize) {
    return null;
  }
  return Math.max(safeSize, 1);
};

const Actionable = () => {
  const query = useQuery();
  const [refresh, setRefresh] = useState(true);
  const [tickets] = useActionableTicketReminder('audio', query.type, [refresh]);
  const flash = useMemo(() => {
    return _.some(tickets, (item) => item.count > 0);
  }, [tickets]);
  const classes = useStyles({ flash });
  const history = useHistory();
  const [actionableSetting] = useActionableSettings();
  const loginInfo = useSelector((state) => state.loginInfo);
  const cardContainerRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const [cardSize, setCardSize] = useState(null);

  const allTickets = useMemo(() => {
    return _.map(tickets, (item) => ({
      ...item,
      ...ACTIONABLE_TICKET_MAPPING[item.ticketType],
      title: item.desc,
      onClick: () =>
        history.push(
          `/actionableDashboard${query.type === 'BS' ? 'Bs' : 'Adm'}?type=${query.type}&ticketType=${
            item.ticketType
          }&tableName=${item.tableName}`
        ),
      disabled: item.count === 0,
    }));
  }, [tickets]);

  const updateCardSize = useCallback(() => {
    // API未返回card时先清空动态尺寸，等数量回来后再重算。
    if (allTickets.length <= 0) {
      setCardSize(null);
      return;
    }

    // 等浏览器完成布局后再量尺寸，避免刷新时拿到旧的容器高度。
    window.cancelAnimationFrame(resizeFrameRef.current);
    resizeFrameRef.current = window.requestAnimationFrame(() => {
      setCardSize(getFittedCardSize(cardContainerRef.current, allTickets.length));
    });
  }, [allTickets.length]);

  // 如果在当前页面切换了用户或者角色，且没有权限访问admission，则跳转到home page
  useEffect(() => {
    const hasAdmissionPermission = userRoleFunc?.getIsShowAdmission();
    // logout的状态也需要能看到admission
    if (!hasAdmissionPermission && loginInfo?.flag) {
      history.push(rou.homeRou.path);
    }
  }, [loginInfo]);

  useLayoutEffect(() => {
    updateCardSize();
    const resizeObserver = window.ResizeObserver ? new window.ResizeObserver(updateCardSize) : null;

    if (resizeObserver && cardContainerRef.current) {
      resizeObserver.observe(cardContainerRef.current);
    } else {
      window.addEventListener('resize', updateCardSize);
    }

    return () => {
      window.cancelAnimationFrame(resizeFrameRef.current);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', updateCardSize);
    };
  }, [updateCardSize, flash, actionableSetting.flashAlert]);

  return (
    <div className={classes.root}>
      <ActionableHeader flash={flash} setRefresh={setRefresh} />
      <Box
        ref={cardContainerRef}
        className={flash && actionableSetting.flashAlert ? classes.flash : classes.noflash}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexWrap="wrap"
        flex={1}
        minHeight={0}
        boxSizing="border-box"
        alignContent="start"
        p="2rem"
        gridGap="3.75rem"
        // paddingTop="8rem"
      >
        {allTickets.map((item) => (
          <ActionableCard key={item.ticketType} {...item} cardSize={cardSize} />
        ))}
      </Box>
    </div>
  );
};

export default Actionable;
